import * as RadixTooltip from '@radix-ui/react-tooltip'
import type { ComponentPropsWithoutRef, ReactNode } from 'react'

import { cn } from './cn'
import { usePortalThemeClass } from './theme-scope'

/**
 * Mounted once, at the root.
 *
 * The delay lives here so every tooltip in the app opens on the same beat, and
 * `skipDelayDuration` is what makes a row of icon buttons feel like one control
 * strip: once a tooltip has opened, moving to the next shows it at once.
 */
export function TooltipProvider({ children }: { children: ReactNode }) {
  return (
    <RadixTooltip.Provider delayDuration={400} skipDelayDuration={200}>
      {children}
    </RadixTooltip.Provider>
  )
}

/** Which edge of the trigger the tooltip sits on. */
export type TooltipSide = 'top' | 'right' | 'bottom' | 'left'

/** What a surface may say about one tooltip, as a caller hands it on. */
export type TooltipOptions = {
  label?: string
  side?: TooltipSide
  /** Overrides the app-wide beat for this one tooltip. `0` opens it on
   *  contact. Left out, it follows `TooltipProvider`. */
  delay?: number
}

/**
 * Transparent to the props it is handed: a tooltip is often the child of
 * another trigger — a menu, a popover — and that parent clones its own props
 * onto this element. Swallowing them here would leave the control opening
 * nothing, with no error to go on.
 *
 * Without a `label` it renders the child alone, so a control whose tooltip is
 * optional wraps unconditionally instead of branching on it.
 */
export function Tooltip({
  label,
  side = 'bottom',
  delay,
  children,
  ...trigger
}: TooltipOptions & { children: ReactNode } & Omit<
    ComponentPropsWithoutRef<typeof RadixTooltip.Trigger>,
    'asChild' | 'children'
  >) {
  const themeClass = usePortalThemeClass()

  if (!label) return children

  return (
    <RadixTooltip.Root delayDuration={delay}>
      <RadixTooltip.Trigger asChild {...trigger}>
        {children}
      </RadixTooltip.Trigger>
      <RadixTooltip.Portal>
        <RadixTooltip.Content
          side={side}
          sideOffset={6}
          // A long label, such as a whole list, wraps at this width.
          className={cn(
            themeClass,
            'z-50 max-w-xs select-none rounded-md bg-inverse px-2 py-1 text-xs text-inverse-foreground shadow-md',
          )}
        >
          {label}
          <RadixTooltip.Arrow className="fill-inverse" />
        </RadixTooltip.Content>
      </RadixTooltip.Portal>
    </RadixTooltip.Root>
  )
}
